import { useI18n } from '../i18n';
import { useRoutes } from '../hooks/useRoutes';
import { Link } from 'react-router-dom';
import { getAvailableTechniqueSlugs } from '../data/techniquePuzzles';
import { TECHNIQUES } from '../pages/EducationPage';
import './HomeSeoBlocks.css';

/**
 * Anasayfa altına gelen 4 SEO bloğu:
 *  - Sudoku Nedir?
 *  - Neden Sudoku Area?
 *  - Popüler Teknikler (internal linking)
 *  - SSS
 *
 * Tüm metin shared/src/i18n/{locale}.json'daki `homeSeo.*` key'lerinden gelir
 * (N-dil desteği için — eski `en ? 'EN' : 'TR'` ternary deseni kaldırıldı).
 */

// Anasayfada gösterilecek "popüler" teknik öncelik listesi (slug bazlı).
// İsimler EducationPage TECHNIQUES + i18n'den okunur — DRY.
const POPULAR_SLUGS = [
  'naked-single', 'hidden-single', 'naked-pair', 'hidden-pair',
  'pointing', 'box-line-reduction', 'x-wing', 'swordfish',
  'xy-wing', 'xyz-wing', 'w-wing', 'simple-coloring',
  'unique-rectangle',
];

export function HomeSeoBlocks() {
  const { t } = useI18n();
  const { r } = useRoutes();
  const s = t.homeSeo as any;

  // Sadece (a) gerçekten üretilmiş puzzle'ı olan ve (b) TECHNIQUES'te tanımlı olanları göster
  const availableSlugs = new Set(getAvailableTechniqueSlugs());
  const visibleTechniques = POPULAR_SLUGS
    .map(slug => TECHNIQUES.find(tech => tech.id === slug))
    .filter((tech): tech is NonNullable<typeof tech> => !!tech && availableSlugs.has(tech.id));

  return (
    <section className="home-seo">
      {/* ── Sudoku Nedir? ── */}
      <div className="home-seo-block">
        <h2>{s.whatIsTitle}</h2>
        <p>{s.whatIsP1}</p>
        <p>
          <Link to={r('tutorial')} className="home-seo-link">{s.tutorialCta}</Link>
        </p>
      </div>

      {/* ── Neden Sudoku Area? ── */}
      <div className="home-seo-block">
        <h2>{s.whyTitle}</h2>
        <ul className="home-seo-features">
          <li><strong>{s.whyF1Title}</strong>{s.whyF1Desc}</li>
          <li><strong>{s.whyF2Title}</strong>{s.whyF2Desc}</li>
          <li><strong>{s.whyF3Title}</strong>{s.whyF3Desc}</li>
          <li><strong>{s.whyF4Title}</strong>{s.whyF4Desc}</li>
          <li><strong>{s.whyF5Title}</strong>{s.whyF5Desc}</li>
        </ul>
      </div>

      {/* ── Popüler Teknikler (internal linking goldmine) ── */}
      {visibleTechniques.length > 0 && (
        <div className="home-seo-block">
          <h2>{s.popularTitle}</h2>
          <p>{s.popularDesc}</p>
          <div className="home-seo-tech-grid">
            {visibleTechniques.map(tech => (
              <Link
                key={tech.id}
                to={r('education', tech.id)}
                className="home-seo-tech-chip"
              >
                {(t.edu.techniques as any)[tech.id]?.name}
              </Link>
            ))}
          </div>
          <p style={{ marginTop: '1rem' }}>
            <Link to={r('education')} className="home-seo-link">{s.seeAllCta}</Link>
          </p>
        </div>
      )}

      {/* ── SSS ── */}
      <div className="home-seo-block">
        <h2>{s.faqTitle}</h2>

        <details className="home-seo-faq">
          <summary>{s.faq1Q}</summary>
          <p>{s.faq1A}</p>
        </details>

        <details className="home-seo-faq">
          <summary>{s.faq2Q}</summary>
          <p>
            {s.faq2Pre}{' '}
            <Link to={r('leaderboard')}>{s.faq2LeaderboardLink}</Link>{' '}
            {s.faq2Mid}{' '}
            <Link to={r('daily')}>{s.faq2DailyLink}</Link>{' '}
            {s.faq2Post}
          </p>
        </details>

        <details className="home-seo-faq">
          <summary>{s.faq3Q}</summary>
          <p>
            {s.faq3Pre}{' '}
            <Link to={r('solver')}>{s.faq3SolverLink}</Link>{' '}
            {s.faq3Post}
          </p>
        </details>

        <details className="home-seo-faq">
          <summary>{s.faq4Q}</summary>
          <p>
            {s.faq4Pre}{' '}
            <Link to={r('education')}>{s.faq4EducationLink}</Link>{' '}
            {s.faq4Post}
          </p>
        </details>

        <details className="home-seo-faq">
          <summary>{s.faq5Q}</summary>
          <p>{s.faq5A}</p>
        </details>
      </div>
    </section>
  );
}
